// components/CreateBookReviewTemplate/index.tsx
import React, { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { Row, Col, Form, FormGroup, Input, Label } from 'reactstrap';
import { Btn, H2, H5 } from '../../../AbstractElements';
import { useCreateBookReviewTemplate, QuestionFormItem } from '../../../hooks/bookReviewTemplate/useCreateBookReviewTemplate';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useFetchGroups } from '../../../hooks/useFetchGroups';
import { useDispatch } from 'react-redux';
import { setReviewDetails } from '../../../ReduxToolkit/ReviewReducers/ReviewReducer';
import { useSelector } from 'react-redux';
import { group } from 'console';

type AssignmentForm = {
    title?: string;
    assignedAt?: string;    // ISO date string
    dueAt?: string;
    visibleFrom?: string;
    visibleTo?: string;
    allowDrafts?: boolean;
    allowDuringNotes?: boolean;
    status?: 'active' | 'closed' | 'archived';
    // groupId is taken from top-level group select, so not included here
};

type FormValues = {
    title: string;
    description?: string;
    tags?: string[]; // we'll accept comma-separated string in UI and parse to array
    group?: string; // selected group id (optional)
    createAssignment?: boolean;
    assignment?: AssignmentForm;
    sections: {
        pre: QuestionFormItem[];
        during: QuestionFormItem[];
        post: QuestionFormItem[];
    };
};

const QUESTION_TYPE_OPTIONS = [
    'text',
    'textarea',
    'select',
    'multi',
    'number',
    'date',
    'boolean',
    'rating',
    'timestampedNotes'
] as const;

export default function CreateBookReviewTemplate() {
    const reviewDetails = useSelector((state: any) => state?.bookReview?.editReviewDetails); // Access review details from Redux
    const { handleCreateTemplate, loading } = useCreateBookReviewTemplate(reviewDetails && reviewDetails._id ? { _id: reviewDetails._id } : null);

    const [tagsInput, setTagsInput] = useState<string>('');

    const dispatch = useDispatch();
    const location = useLocation();


    console.log(JSON.stringify(reviewDetails));
    if (location.pathname.includes('/book_review/create')) {
        dispatch(setReviewDetails(null));
    }

    const user = JSON.parse(localStorage.getItem('user') || '{}') as { id?: string };
    const { userSpecificGroups } = useFetchGroups(user?.id || '');

    const normalizedDate = (dateStr?: string) => {
        // alert(typeof(dateStr))
        if (!dateStr) return '';
        return new Date(dateStr || '')?.toISOString()?.split('T')[0];
    }

    const { control, handleSubmit, setError, setValue, getValues, watch, formState: { errors } } = useForm<FormValues>({
        mode: 'onChange',
        defaultValues: (reviewDetails) ? {
            ...reviewDetails,
            createAssignment: true,
            assignment: {
                assignedAt: normalizedDate(reviewDetails?.assignments[0]?.assignedAt),
                visibleFrom: normalizedDate(reviewDetails?.assignments[0]?.visibleFrom) || "",
                visibleTo: normalizedDate(reviewDetails?.assignments[0]?.visibleTo),
                dueAt: normalizedDate(reviewDetails?.assignments[0]?.dueAt) || "",
                title: reviewDetails?.assignments[0]?.title,
                status: reviewDetails?.assignments[0]?.status,
                _id: reviewDetails?.assignments[0]?._id,
                allowDrafts: reviewDetails?.assignments[0]?.allowDrafts,
                allowDuringNotes: reviewDetails?.assignments[0]?.allowDuringNotes,
            },
            sections: {
                pre: (reviewDetails.sections?.pre ?? []).map((q: any) => ({ ...q, optionsStr: (q.options || []).map((o: any) => o.value).join(',') })),
                during: (reviewDetails.sections?.during ?? []).map((q: any) => ({ ...q, optionsStr: (q.options || []).map((o: any) => o.value).join(',') })),
                post: (reviewDetails.sections?.post ?? []).map((q: any) => ({ ...q, optionsStr: (q.options || []).map((o: any) => o.value).join(',') }))
            }
        } : {
            title: '',
            description: '',
            tags: [],
            group: '',
            createAssignment: false,
            assignment: {
                title: '',
                assignedAt: '',
                dueAt: '',
                visibleFrom: '',
                visibleTo: '',
                allowDrafts: true,
                allowDuringNotes: true,
                status: 'active'
            },
            sections: { pre: [], during: [], post: [] }
        }
    });

    // local UI state to toggle the assignment card - kept so existing JSX that references it remains unchanged
    const [assignmentVisible, setAssignmentVisible] = useState<boolean>(false);

    // keep assignmentVisible in sync with the form value for createAssignment
    const watchedCreateAssignment = watch('createAssignment');
    useEffect(() => {
        setAssignmentVisible(!!watchedCreateAssignment);
    }, [watchedCreateAssignment]);

    useEffect(() => {
        if (!Array.isArray(userSpecificGroups) || userSpecificGroups.length === 0) return;

        const currentGroup = getValues('group');
        if (currentGroup) return; // user already selected something

        const first = userSpecificGroups[0] as any;
        const firstId = first?.id ?? first?._id ?? '';
        if (firstId) {
            setValue('group', String(firstId));
        }
    }, [userSpecificGroups, getValues, setValue]);

    const pre = useFieldArray({ control, name: 'sections.pre' });
    const during = useFieldArray({ control, name: 'sections.during' });
    const post = useFieldArray({ control, name: 'sections.post' });

    const buildOptionsArray = (optionsStr?: string) => {
        if (!optionsStr) return [];
        return optionsStr.split(',').map(s => {
            const v = s.trim();
            return { value: v, label: v };
        }).filter(o => o.value);
    };

    const onSubmit: SubmitHandler<FormValues> = (data) => {


        // check qid uniqueness
        const allQids = [
            ...(data.sections.pre || []).map(q => q.qid),
            ...(data.sections.during || []).map(q => q.qid),
            ...(data.sections.post || []).map(q => q.qid)
        ].filter(Boolean as any);

        const duplicates = allQids.filter((v, i, a) => a.indexOf(v) !== i);
        if (duplicates.length) {
            const uniqueDup = Array.from(new Set(duplicates)).join(', ');
            setError('title', { type: 'manual', message: `Duplicate qid(s): ${uniqueDup}. Ensure each question has unique qid.` });
            return;
        }

        // normalize tags
        const normalizedTags = (Array.isArray(data.tags) ? data.tags : [])
            .map(t => (t || '').trim().toLowerCase())
            .filter(Boolean);

        // transform sections
        const buildOptionsArray = (optionsStr?: string) =>
            !optionsStr ? [] : optionsStr.split(',').map(s => {
                const v = s.trim();
                return { value: v, label: v };
            }).filter(o => o.value);

        const transformSection = (arr: QuestionFormItem[]) => arr.map((q, idx) => ({
            qid: q.qid,
            label: q.label,
            type: q.type,
            required: !!q.required,
            placeholder: q.placeholder || '',
            options: buildOptionsArray(q.optionsStr),
            order: typeof q.order === 'number' ? q.order : idx
        }));

        // ✅ always include assignment (mandatory)
        const a = data.assignment || {};
        const payload: any = {
            title: data.title,
            description: data.description || '',
            tags: normalizedTags,
            // groupId: data.group || '', // group required from UI
            sections: {
                pre: transformSection(data.sections.pre || []),
                during: transformSection(data.sections.during || []),
                post: transformSection(data.sections.post || [])
            },
            assignment: {
                ...((reviewDetails && reviewDetails?.assignments && reviewDetails?.assignments[0] && reviewDetails?.assignments[0]?._id)
                    ? { _id: reviewDetails?.assignments[0]?._id }
                    : {}),
                title: a.title || `${data.title} — Assignment`,
                assignedAt: a.assignedAt || undefined,
                dueAt: a.dueAt || undefined,
                visibleFrom: a.visibleFrom || undefined,
                visibleTo: a.visibleTo || undefined,
                allowDrafts: typeof a.allowDrafts === 'boolean' ? a.allowDrafts : true,
                allowDuringNotes: typeof a.allowDuringNotes === 'boolean' ? a.allowDuringNotes : true,
                status: a.status || 'active',
                groupId: data.group // ensure assignment has groupId
            }
        };

        handleCreateTemplate(payload);
    };



    // helper array describing sections and their fieldArray + base name
    const sectionsMeta = [
        { title: 'Pre Discussion Questions', fieldArray: pre, name: 'sections.pre' as const },
        { title: 'In Group Questions', fieldArray: during, name: 'sections.during' as const },
        { title: 'Post Discussion Questions', fieldArray: post, name: 'sections.post' as const }
    ] as const;

    // Scoped styles (kept inside component so you can drop this file without touching external CSS)
    const styles = `
  /* Container */
  .template-builder {
    max-width: 1200px;
    margin: 0px auto;
    padding: 18px;
  }

  /* Scroll area */
  .template-scroll {
    max-height: calc(100vh - 220px);
    overflow: auto;
    padding-right: 8px;
  }

  /* Cards */
  .template-card {
    background: rgba(255,255,255,0.03);
    border-radius: 12px;
    padding: 18px;
    box-shadow: 0 6px 18px rgba(0,0,0,0.6);
    border: 1px solid rgba(255,255,255,0.03);
    margin-bottom: 18px;
  }

  .section-card {
    background: rgba(0,0,0,0.35);
    border-radius: 10px;
    padding: 14px;
    margin-top: 12px;
    border: 1px solid rgba(255,255,255,0.04);
  }

  .section-header {
    display:flex;
    align-items:center;
    justify-content: space-between;
    gap: 12px;
  }

  .section-title {
    font-weight: 600;
    color: #f7f7f7;
  }

  /* Question card */
  .question-card {
    background: rgba(255,255,255,0.02);
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 12px;
    border: 1px solid rgba(255,255,255,0.04);
  }

  .question-grid {
    display: grid;
    grid-template-columns: 1fr 2fr 160px 64px auto;
    gap: 12px;
    align-items: start;
  }

  @media (max-width: 992px) {
    .question-grid {
      grid-template-columns: 1fr;
    }
    .two-col {
      display:block;
    }
  }

  .placeholder-row {
    display:flex;
    gap:12px;
    margin-top:10px;
  }

  .placeholder-row .col {
    flex:1;
  }

  /* Buttons */
  .btn-add {
    background: linear-gradient(90deg, #ff5a3c, #ff3b1f);
    border: none;
    color: #fff;
    padding: 8px 14px;
    border-radius: 8px;
    box-shadow: 0 6px 12px rgba(255,59,31,0.12);
  }
  .btn-add:hover { opacity: 0.95; }

  .btn-remove {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.06);
    color: #ff6b4a;
    padding: 8px 12px;
    border-radius: 8px;
  }
  .btn-remove:hover { background: rgba(255,255,255,0.02); }

  /* Inputs: make them full width with comfortable padding */
  .template-card .form-control,
  .section-card .form-control,
  .question-card .form-control {
    background: rgba(0,0,0,0.55);
    color: #eee;
    border-radius: 6px;
    border: 1px solid rgba(255,255,255,0.04);
    padding: 10px 12px;
  }

  /* small label tweaks */
  .section-card label {
    color: #cfcfcf;
    font-size: 0.9rem;
    font-weight: 600;
  }

  /* custom scrollbar (scoped) */
  .template-scroll::-webkit-scrollbar { width: 10px; }
  .template-scroll::-webkit-scrollbar-track { background: transparent; }
  .template-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.06); border-radius: 10px; }
  .template-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.09); }

  /* top-level header & actions align */
  .top-actions { display:flex; gap:12px; align-items:center; justify-content:flex-end; }

  /* Responsive tweaks */
  @media (max-width: 768px) {
    .question-grid { gap: 8px; }
    .btn-add, .btn-remove { padding: 6px 10px; }
  }
  `;

    const watchedTags = watch('tags');
    useEffect(() => {
        if (Array.isArray(watchedTags)) {
            // show join with commas; don't force a trailing comma here
            setTagsInput(watchedTags.join(','));
        } else {
            setTagsInput(watchedTags ?? '');
        }
    }, [watchedTags]);


    return (
        // <>
        // </>

        <div className="template-builder">
            <style>{styles}</style>

            <div className="template-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <div>
                        <H2 className="f-w-600 mb-2" style={{ margin: 0 }}>Create Book Review</H2>
                        {/* <div style={{ color: '#bdbdbd', fontSize: 13 }}>Build reusable templates for group book reviews</div> */}
                    </div>
                </div>

                <div className="template-scroll">
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        {/* Main info card */}
                        <div className="section-card">
                            <Row>
                                <Col md={4}>
                                    <FormGroup>
                                        <Label>Book Title</Label>
                                        <Controller
                                            name="title"
                                            control={control}
                                            rules={{ required: 'Template title is required' }}
                                            render={({ field }) => (
                                                <Input
                                                    type="text"
                                                    placeholder="Enter title"
                                                    className="form-control"
                                                    value={typeof field.value === 'string' || typeof field.value === 'number' ? field.value : ''}
                                                    onChange={(e) => field.onChange(e.target.value)}
                                                    onBlur={field.onBlur}
                                                    name={field.name}
                                                    innerRef={field.ref as any}
                                                />
                                            )}
                                        />
                                        {errors.title && <div style={{ color: '#ffb4a7', marginTop: 8 }}>{errors.title.message}</div>}
                                    </FormGroup>
                                </Col>



                                <Col md={4}>
                                    <FormGroup>
                                        <Label>Tags (comma separated)</Label>
                                        <Controller
                                            name="tags"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    type="text"
                                                    placeholder="e.g. reflection,habits,group-discussion"
                                                    value={tagsInput}
                                                    onChange={(e) => {
                                                        // only update local input string while typing — keep the comma the user types
                                                        setTagsInput(e.target.value);
                                                    }}
                                                    onBlur={() => {
                                                        // when user leaves the field, parse the string into array and update form state
                                                        const arr = tagsInput
                                                            .split(',')
                                                            .map(s => s.trim().toLowerCase())
                                                            .filter(Boolean);
                                                        field.onChange(arr);
                                                        // also call the field.onBlur so react-hook-form knows about blur
                                                        if (typeof field.onBlur === 'function') field.onBlur();
                                                    }}
                                                    name={field.name}
                                                    innerRef={field.ref as any}
                                                />
                                            )}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row className="mt-2">
                                <Col>
                                    <FormGroup>
                                        <Label>Description</Label>
                                        <Controller
                                            name="description"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    type="textarea"
                                                    rows={3}
                                                    placeholder="Short description (optional)"
                                                    value={field.value ?? ''}
                                                    onChange={(e) => field.onChange(e.target.value)}
                                                    onBlur={field.onBlur}
                                                    name={field.name}
                                                    innerRef={field.ref as any}
                                                />
                                            )}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            {/* assignment toggle */}
                            {/* <Row className="mt-2">
                <Col md={12}>
                  <FormGroup>
                    <Controller name="createAssignment" control={control} render={({ field }) => (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <input id="create-assignment" type="checkbox" checked={!!field.value} onChange={(e) => { field.onChange(e.target.checked); setAssignmentVisible(e.target.checked); }} />
                        <Label style={{ marginBottom: 0 }} htmlFor="create-assignment">Create assignment for this template now</Label>
                      </div>
                    )} />
                  </FormGroup>
                </Col>
              </Row> */}


                            <div className="section-card" style={{ marginTop: 12 }}>
                                <Row>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Assignment Title</Label>
                                            <Controller name="assignment.title" control={control} render={({ field }) => (
                                                <Input type="text" placeholder="Assignment title (optional)" value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value)} />
                                            )} />
                                        </FormGroup>
                                    </Col>

                                    <Col md={2}>
                                        <FormGroup>
                                            <Label>Assign Date</Label>
                                            <Controller name="assignment.assignedAt"
                                                control={control}
                                                rules={{ required: 'Assign Date is required' }}

                                                render={({ field }) => (
                                                    <Input type="date" value={field.value ? (field.value as string).split('T')[0] : ''} onChange={(e) => field.onChange(e.target.value)} />
                                                )} />
                                            {errors.assignment?.assignedAt && (
                                                <div style={{ color: '#ffb4a7', marginTop: 8 }}>
                                                    {(errors.assignment as any).assignedAt?.message}
                                                </div>
                                            )}
                                        </FormGroup>
                                    </Col>

                                    <Col md={2}>
                                        <FormGroup>
                                            <Label>Due Date</Label>
                                            <Controller name="assignment.dueAt"
                                                control={control}
                                                rules={{ required: 'Due Date is required' }}
                                                render={({ field }) => (
                                                    <Input type="date" value={field.value ? (field.value as string).split('T')[0] : ''} onChange={(e) => field.onChange(e.target.value)} />
                                                )} />
                                            {errors.assignment?.dueAt && (
                                                <div style={{ color: '#ffb4a7', marginTop: 8 }}>
                                                    {(errors.assignment as any).dueAt?.message}
                                                </div>
                                            )}
                                        </FormGroup>
                                    </Col>

                                    <Col md={2}>
                                        <FormGroup>
                                            <Label>Visible From</Label>
                                            <Controller
                                                name="assignment.visibleFrom"
                                                rules={{ required: 'Visible From is required' }}

                                                control={control}
                                                render={({ field }) => (
                                                    <Input type="date" value={field.value ? (field.value as string).split('T')[0] : ''} onChange={(e) => field.onChange(e.target.value)} />
                                                )} />
                                            {errors.assignment?.visibleFrom && (
                                                <div style={{ color: '#ffb4a7', marginTop: 8 }}>
                                                    {(errors.assignment as any).visibleFrom?.message}
                                                </div>
                                            )}
                                        </FormGroup>
                                    </Col>

                                    <Col md={2}>
                                        <FormGroup>
                                            <Label>Visible To</Label>
                                            <Controller
                                                name="assignment.visibleTo"
                                                control={control}
                                                rules={{ required: 'Visible To is required' }}

                                                render={({ field }) => (
                                                    <Input type="date" value={field.value ? (field.value as string).split('T')[0] : ''} onChange={(e) => field.onChange(e.target.value)} />
                                                )} />
                                            {errors.assignment?.visibleTo && (
                                                <div style={{ color: '#ffb4a7', marginTop: 8 }}>
                                                    {(errors.assignment as any).visibleTo?.message}
                                                </div>
                                            )}
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row className="mt-2">
                                    {/* <Col md={3}>
                      <FormGroup>
                        <Label>Allow Drafts</Label>
                        <Controller name="assignment.allowDrafts" control={control} defaultValue={true} render={({ field }) => (
                          <Input type="checkbox" checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
                        )} />
                      </FormGroup>
                    </Col> */}

                                    {/* <Col md={3}>
                      <FormGroup>
                        <Label>Allow During Notes</Label>
                        <Controller name="assignment.allowDuringNotes" control={control} defaultValue={true} render={({ field }) => (
                          <Input type="checkbox" checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
                        )} />
                      </FormGroup>
                    </Col> */}

                                    <Col md={3}>
                                        <FormGroup>
                                            <Label>Status</Label>
                                            <Controller name="assignment.status" control={control} defaultValue="active" render={({ field }) => (
                                                <Input type="select" value={field.value as any} onChange={(e) => field.onChange(e.target.value)}>
                                                    <option value="active">active</option>
                                                    <option value="closed">closed</option>
                                                    <option value="archived">archived</option>
                                                </Input>
                                            )} />
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Select Group</Label>
                                            <Controller
                                                name="group"
                                                control={control}
                                                rules={{ required: 'Group is required' }}
                                                render={({ field }) => (
                                                    <Input
                                                        type="select"
                                                        className="form-control"
                                                        value={String(field.value ?? '')}
                                                        onChange={(e) => field.onChange(e.target.value)}
                                                        onBlur={field.onBlur}
                                                        name={field.name}
                                                        innerRef={field.ref as any}
                                                    >
                                                        {/* If groups exist, show select + options; otherwise show helpful placeholder */}
                                                        {Array.isArray(userSpecificGroups) && userSpecificGroups.length > 0 ? (
                                                            <>
                                                                <option value="">— Select group —</option>
                                                                {userSpecificGroups.map((g) => (
                                                                    <option key={g.id ?? (g as any)._id} value={g.id ?? (g as any)._id}>
                                                                        {g.title ?? (g as any).title ?? (g as any).name ?? 'Untitled Group'}
                                                                    </option>
                                                                ))}
                                                            </>
                                                        ) : (
                                                            <option value="">— No groups available —</option>
                                                        )}
                                                    </Input>
                                                )}
                                            />
                                            {/* show validation */}
                                            {errors.group && <div style={{ color: '#ffb4a7', marginTop: 8 }}>{errors.group.message}</div>}

                                        </FormGroup>
                                    </Col>
                                </Row>
                            </div>




                        </div>

                        {/* Sections */}
                        {sectionsMeta.map(({ title, fieldArray, name }, sIdx) => (
                            <div className="section-card" key={title}>
                                <div className="section-header">
                                    <div>
                                        <div className="section-title">{title}</div>
                                        <div style={{ color: '#bdbdbd', fontSize: 12, marginTop: 6 }}>Add questions that participants will answer {title.toLowerCase().includes('pre') ? 'before the session' : title.toLowerCase().includes('post') ? 'after the session' : 'during the session'}.</div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <button
                                            type="button"
                                            className="btn-add"
                                            onClick={() =>
                                                fieldArray.append({
                                                    qid: `${title.toLowerCase().split(/\s+/).join('_')}_q${(fieldArray.fields.length || 0) + 1}`,
                                                    label: '',
                                                    type: 'text',
                                                    required: false,
                                                    placeholder: '',
                                                    optionsStr: '',
                                                    order: fieldArray.fields.length
                                                })
                                            }
                                        >
                                            + Add Question
                                        </button>
                                    </div>
                                </div>

                                <div style={{ marginTop: 12 }}>
                                    {fieldArray.fields.length === 0 && (
                                        <div style={{ color: '#bdbdbd', padding: '10px 8px' }}>No questions yet. Use <strong>+ Add Question</strong> to start.</div>
                                    )}

                                    {fieldArray.fields.map((item, idx) => (
                                        <div key={item.id} className="question-card">
                                            <div className="question-grid">
                                                {/* <div>
                                                    <Label>QID</Label>
                                                    <Controller
                                                        control={control}
                                                        name={`${name}.${idx}.qid`}
                                                        rules={{ required: 'qid required' }}
                                                        render={({ field }) => (
                                                            <Input
                                                                type="text"
                                                                placeholder="unique qid"
                                                                value={field.value ?? ''}
                                                                onChange={(e) => field.onChange(e.target.value)}
                                                                onBlur={field.onBlur}
                                                                name={field.name}
                                                                innerRef={field.ref as any}
                                                            />
                                                        )}
                                                    />
                                                    {errors?.sections && (errors as any).sections?.[sIdx] && <div style={{ color: '#ffb4a7', marginTop: 6 }}>{(errors as any).sections?.[sIdx]?.qid?.message}</div>}
                                                </div> */}

                                                <div>
                                                    <Label>Label</Label>
                                                    <Controller
                                                        control={control}
                                                        name={`${name}.${idx}.label`}
                                                        rules={{ required: 'Question label required' }}
                                                        render={({ field }) => (
                                                            <Input
                                                                type="text"
                                                                placeholder="Question label"
                                                                value={field.value ?? ''}
                                                                onChange={(e) => field.onChange(e.target.value)}
                                                                onBlur={field.onBlur}
                                                                name={field.name}
                                                                innerRef={field.ref as any}
                                                            />
                                                        )}
                                                    />
                                                </div>

                                                <div>
                                                    <Label>Type</Label>
                                                    <Controller
                                                        control={control}
                                                        name={`${name}.${idx}.type`}
                                                        render={({ field }) => (
                                                            <Input
                                                                type="select"
                                                                value={field.value ?? 'text'}
                                                                onChange={(e) => field.onChange(e.target.value)}
                                                                onBlur={field.onBlur}
                                                                name={field.name}
                                                                innerRef={field.ref as any}
                                                            >
                                                                {QUESTION_TYPE_OPTIONS.map((t) => (
                                                                    <option key={t} value={t}>
                                                                        {t}
                                                                    </option>
                                                                ))}
                                                            </Input>
                                                        )}
                                                    />
                                                </div>

                                                <div>
                                                    <Label>Req</Label>
                                                    <Controller
                                                        control={control}
                                                        name={`${name}.${idx}.required`}
                                                        render={({ field }) => (
                                                            <Input
                                                                type="checkbox"
                                                                checked={!!field.value}
                                                                onChange={(e) => field.onChange(e.target.checked)}
                                                                onBlur={field.onBlur}
                                                                name={field.name}
                                                                innerRef={field.ref as any}
                                                                style={{ width: 20, height: 20 }}
                                                            />
                                                        )}
                                                    />
                                                </div>

                                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                    <button type="button" className="btn-remove" onClick={() => fieldArray.remove(idx)}>
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="placeholder-row">
                                                <div className="col">
                                                    <Label>Placeholder</Label>
                                                    <Controller
                                                        control={control}
                                                        name={`${name}.${idx}.placeholder`}
                                                        render={({ field }) => (
                                                            <Input
                                                                type="text"
                                                                placeholder="Placeholder (optional)"
                                                                value={field.value ?? ''}
                                                                onChange={(e) => field.onChange(e.target.value)}
                                                                onBlur={field.onBlur}
                                                                name={field.name}
                                                                innerRef={field.ref as any}
                                                            />
                                                        )}
                                                    />
                                                </div>

                                                <div className="col">
                                                    <Label>Options (comma separated — only used for select/multi)</Label>
                                                    <Controller
                                                        control={control}
                                                        name={`${name}.${idx}.optionsStr`}
                                                        render={({ field }) => (
                                                            <Input
                                                                type="text"
                                                                placeholder="option1, option2, option3"
                                                                value={field.value ?? ''}
                                                                onChange={(e) => field.onChange(e.target.value)}
                                                                onBlur={field.onBlur}
                                                                name={field.name}
                                                                innerRef={field.ref as any}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}

                        {/* Bottom actions */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 18 }}>
                            <Btn type="button" color="danger">
                                <Link className="d-flex align-items-center" to={`${process.env.PUBLIC_URL}/book_review/template_list`} style={{ color: '#fff', textDecoration: 'none' }}>
                                    Cancel
                                </Link>
                            </Btn>

                            <Btn type="submit" color="success" className="btn-add" disabled={loading}>
                                {loading ? 'Saving...' : (reviewDetails ? 'Update Template' : 'Create Template')}
                            </Btn>
                        </div>
                    </Form>
                </div>
            </div>
        </div>
    );
}
